import { Box, Button, Flex, Text, Textarea } from '@rocket/ui';
import { diffWordsWithSpace } from 'diff';
import { useMemo, useState } from 'react';
import { LuCheck, LuChevronLeft, LuChevronRight, LuPencil } from 'react-icons/lu';

const MAX_SAMPLE_EDITS = 5;

export function SampleEditsCarousel({
  edits,
  onEditChange,
}: {
  edits: Array<{
    before: string;
    after: string;
    contextBefore?: string;
    contextFollowing?: string;
  }>;
  onEditChange?: (updated: {
    before: string;
    after: string;
    contextBefore?: string;
    contextFollowing?: string;
  }) => void;
}) {
  const [index, setIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editBefore, setEditBefore] = useState('');
  const [editAfter, setEditAfter] = useState('');
  const capped = edits.slice(0, MAX_SAMPLE_EDITS);
  const current = capped[index];

  const hasContext = !!(current.contextBefore || current.contextFollowing);
  const diffParts = useMemo(() => diffWordsWithSpace(current.before, current.after), [current]);

  const startEditing = () => {
    setEditBefore(current.before);
    setEditAfter(current.after);
    setIsEditing(true);
  };

  const saveEdit = () => {
    setIsEditing(false);
    onEditChange?.({
      before: editBefore,
      after: editAfter,
      contextBefore: current.contextBefore,
      contextFollowing: current.contextFollowing,
    });
  };

  return (
    <Box mt="1" p="2" bg="bg.subtle" borderWidth="1px" borderRadius="sm" borderColor="border.muted">
      <Flex.H align="center" justify="space-between" mb="1">
        <Text fontSize="xs" color="fg.subtle">
          Example edit
        </Text>
        <Flex.H align="center" gap="1">
          {onEditChange && !isEditing && (
            <Button size="xs" variant="ghost" onClick={startEditing} aria-label="Edit example">
              <LuPencil size={12} />
            </Button>
          )}
          {isEditing && (
            <Button
              size="xs"
              variant="ghost"
              colorPalette="green"
              onClick={saveEdit}
              aria-label="Save edit"
            >
              <LuCheck size={12} />
            </Button>
          )}
          {capped.length > 1 && (
            <>
              <Button
                size="xs"
                variant="ghost"
                disabled={index === 0}
                onClick={() => setIndex((i) => i - 1)}
                aria-label="Previous example"
              >
                <LuChevronLeft size={12} />
              </Button>
              <Text fontSize="xs" color="fg.subtle">
                {index + 1} / {capped.length}
              </Text>
              <Button
                size="xs"
                variant="ghost"
                disabled={index === capped.length - 1}
                onClick={() => setIndex((i) => i + 1)}
                aria-label="Next example"
              >
                <LuChevronRight size={12} />
              </Button>
            </>
          )}
        </Flex.H>
      </Flex.H>
      {hasContext && current.contextBefore && (
        <Text fontSize="xs" color="fg.subtle" fontStyle="italic" mb="1">
          Insert after: &ldquo;{current.contextBefore}&rdquo;
        </Text>
      )}
      {isEditing ? (
        <Flex.V gap="2">
          <Textarea
            size="xs"
            label="Original text"
            value={editBefore}
            onChange={(e) => setEditBefore(e.target.value)}
            rows={2}
          />
          <Textarea
            size="xs"
            label="Edited text"
            value={editAfter}
            onChange={(e) => setEditAfter(e.target.value)}
            rows={2}
          />
        </Flex.V>
      ) : (
        <Text fontSize="xs" lineHeight="tall">
          {diffParts.map(({ added, removed, value }: (typeof diffParts)[number], i: number) => (
            <Text
              key={i}
              as="span"
              color={added ? 'green.fg' : removed ? 'red.fg' : undefined}
              fontWeight={added ? 'bold' : undefined}
              textDecoration={removed ? 'line-through' : undefined}
            >
              {value}
            </Text>
          ))}
        </Text>
      )}
      {hasContext && current.contextFollowing && (
        <Text fontSize="xs" color="fg.subtle" fontStyle="italic" mt="1">
          Insert before: &ldquo;{current.contextFollowing}&rdquo;
        </Text>
      )}
    </Box>
  );
}
